import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "../../../classes/contexts/AppContext";
import Button from "../../../components/button/Button";
import SvgImageCarousel from "../../../components/svgImageCarousel/SvgImageCarousel";
import WidgetLoader from "../../../components/widgetLoader/WidgetLoader";
import ModalBase from "../uploadModal/modalBase/ModalBase";
import "./PainAssesmentModal.css";

import { ReactComponent as FemaleAnterior } from "../../../assets/images/painAssesment/female_anterior.svg";
import { ReactComponent as FemaleLeft } from "../../../assets/images/painAssesment/female_left.svg";
import { ReactComponent as FemalePosterior } from "../../../assets/images/painAssesment/female_posterior.svg";
import { ReactComponent as FemaleRight } from "../../../assets/images/painAssesment/female_right.svg";
import { ReactComponent as MaleAnterior } from "../../../assets/images/painAssesment/male_anterior.svg";
import { ReactComponent as MaleLeft } from "../../../assets/images/painAssesment/male_left.svg";
import { ReactComponent as MalePosterior } from "../../../assets/images/painAssesment/male_posterior.svg";
import { ReactComponent as MaleRight } from "../../../assets/images/painAssesment/male_right.svg";
import { usePatientContext } from "../../../classes/contexts/PatientContext";
import IconsEnum from "../../../classes/enums/IconsEnum";
import IconButton from "../../../components/iconButton/IconButton";
import PainAssesmentToolSelector from "../../../components/painAssessmentToolSelector/PainAssesmentToolSelector";
import {
  DEFAULT_TOOL,
  getToolConfig,
} from "../../../components/painAssessmentToolSelector/painAssesmentConfig";
import useTranslation from "../../../hooks/i18n/useTranslation";
import {
  addPainAssessment,
  updatePainAssessment,
} from "../../../services/PainAssessmentService";

const PainAssesmentModal = ({ assessment: existingAssessment }) => {
  const { hidePopup } = useAppContext();
  const { patientData, refreshPatientContext } = usePatientContext();

  const isEditMode = !!existingAssessment;
  const [gender] = useState(existingAssessment?.gender || patientData?.gender);
  const [currentOrientation, setCurrentOrientation] = useState(0);
  const drawingAreaRef = useRef(null);

  const [allPaths, setAllPaths] = useState(existingAssessment?.allPaths || []);
  const [canDraw, setCanDraw] = useState(true);
  const [selectedPathIndex, setSelectedPathIndex] = useState(null);
  const [pendingPath, setPendingPath] = useState(null); // Path waiting for qualification
  const [selectedTool, setSelectedTool] = useState(DEFAULT_TOOL);

  const [painLevel, setPainLevel] = useState(0);
  const [painType, setPainType] = useState("");
  const [showQualificationWidget, setShowQualificationWidget] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [canClickToClose, setCanClickToClose] = useState(false);
  const [hoveredPathInfo, setHoveredPathInfo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { t } = useTranslation();

  const painTypesData = useMemo(
    () => [
      { id: "aching", translationKey: "217", fallback: "Aching" },
      { id: "burning", translationKey: "218", fallback: "Burning" },
      { id: "cramping", translationKey: "219", fallback: "Cramping" },
      { id: "numbness", translationKey: "220", fallback: "Numbness" },
      { id: "radiating", translationKey: "221", fallback: "Radiating" },
      { id: "shooting", translationKey: "222", fallback: "Shooting" },
      { id: "stabbing", translationKey: "223", fallback: "Stabbing" },
      { id: "tingling", translationKey: "224", fallback: "Tingling" },
      { id: "none", translationKey: "225", fallback: "None" },
    ],
    []
  );

  const getPainColor = level => {
    if (level === 0) return "#ffffff";

    const rootStyles = getComputedStyle(document.documentElement);
    const colors = [
      rootStyles.getPropertyValue("--MDsim-gradient-dark-green").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-light-green").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-brillant-green").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-pale-green").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-light-yellow").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-yellow").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-orange").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-dark-orange").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-red").trim(),
      rootStyles.getPropertyValue("--MDsim-gradient-dark-red").trim(),
    ];

    return colors[level - 1] || "#ffffff";
  };

  const getSvgComponents = () => {
    const components = {
      Male: [
        { component: MaleAnterior },
        { component: MaleLeft },
        { component: MalePosterior },
        { component: MaleRight },
      ],
      Female: [
        { component: FemaleAnterior },
        { component: FemaleLeft },
        { component: FemalePosterior },
        { component: FemaleRight },
      ],
    };
    return components[gender];
  };

  // Handle new path from carousel
  const handlePathAdded = useCallback(
    (pathCoordinates, orientation, toolType = DEFAULT_TOOL) => {
      const toolConfig = getToolConfig(toolType);

      // Create pending path with white color so it's visible while waiting for qualification
      const pendingPathWithColor = {
        path: pathCoordinates,
        orientation,
        color: "#ffffff",
        painLevel: 0,
        painType: "",
        painTypeId: "",
        toolType: toolType,
        strokeWidth: toolConfig.strokeWidth,
      };

      setPendingPath(pendingPathWithColor);
      setAllPaths(prev => [...prev, pendingPathWithColor]);
      setCanDraw(false);
      setShowQualificationWidget(true);
      setPainLevel(0);
      setPainType("");

      setCanClickToClose(false);
      setTimeout(() => {
        setCanClickToClose(true);
      }, 500);
    },
    []
  );

  const handleDeletePath = () => {
    if (selectedPathIndex !== null) {
      setAllPaths(prev =>
        prev.filter((_, index) => index !== selectedPathIndex)
      );

      setHasChanges(true);

      setShowQualificationWidget(false);
      setCanDraw(true);
      setSelectedPathIndex(null);
      setPendingPath(null);
      setPainLevel(0);
      setPainType("");
    }
  };

  const handlePathsDeleted = useCallback(
    pathIndices => {
      if (pathIndices && pathIndices.length > 0) {
        setAllPaths(prev =>
          prev.filter((_, index) => !pathIndices.includes(index))
        );
        setHasChanges(true);

        // Reset state if deleted path was selected
        if (pathIndices.includes(selectedPathIndex)) {
          setShowQualificationWidget(false);
          setCanDraw(true);
          setSelectedPathIndex(null);
          setPendingPath(null);
        }
      }
    },
    [selectedPathIndex]
  );

  // Handle path click for editing
  const handlePathClick = useCallback(
    (pathIndex, event) => {
      event.preventDefault();
      event.stopPropagation();

      const path = allPaths[pathIndex];
      if (path && path.orientation === currentOrientation) {
        setSelectedPathIndex(pathIndex);
        setPainLevel(path.painLevel || 0);
        const mappedPainTypeId =
          path.painTypeId ||
          painTypesData.find(type => type.fallback === path.painType)?.id ||
          "";
        setPainType(mappedPainTypeId);
        setShowQualificationWidget(true);
        setCanDraw(false);

        setCanClickToClose(false);
        setTimeout(() => {
          setCanClickToClose(true);
        }, 500);
      }
    },
    [allPaths, currentOrientation, painTypesData]
  );

  const handlePathsUpdate = useCallback(updatedPaths => {
    setAllPaths(updatedPaths);
    setHasChanges(true);
  }, []);

  // Add null check after all hooks - show loading instead of null
  if (!patientData) {
    return (
      <ModalBase
        title={t("186")}
        subtitle={t("199")}
        onClose={hidePopup}
        className="pain-assessment-modal"
      >
        <WidgetLoader isLoading={true} />
      </ModalBase>
    );
  }

  const handleOrientationChange = newIndex => {
    setCurrentOrientation(newIndex);

    if (pendingPath) {
      setAllPaths(prev => prev.slice(0, -1));
    }

    setShowQualificationWidget(false);
    setCanDraw(true);
    setSelectedPathIndex(null);
    setPendingPath(null);
    setHoveredPathInfo(null);
  };

  const handleAddPainQualification = () => {
    if (painLevel === 0) return;

    const selectedTypeData = painTypesData.find(type => type.id === painType);

    // When editing an existing path, preserve its tool type
    const existingPath =
      selectedPathIndex !== null ? allPaths[selectedPathIndex] : null;
    const pathToolType =
      pendingPath?.toolType || existingPath?.toolType || selectedTool;
    const toolConfig = getToolConfig(pathToolType);

    const painData = {
      painLevel,
      painType: selectedTypeData ? selectedTypeData.fallback : "",
      painTypeId: selectedTypeData ? selectedTypeData.id : "",
      color: getPainColor(painLevel),
      orientation: currentOrientation,
      // Only update toolType and strokeWidth for new paths
      ...(selectedPathIndex === null && {
        toolType: pathToolType,
        strokeWidth: toolConfig.strokeWidth,
      }),
    };

    if (selectedPathIndex !== null) {
      setAllPaths(prev =>
        prev.map((path, index) =>
          index === selectedPathIndex ? { ...path, ...painData } : path
        )
      );
    } else if (pendingPath) {
      setAllPaths(prev => {
        const newPaths = [...prev];
        newPaths[newPaths.length - 1] = { ...pendingPath, ...painData };
        return newPaths;
      });
    }

    setHasChanges(true);

    setShowQualificationWidget(false);
    setCanDraw(true);
    setSelectedPathIndex(null);
    setPendingPath(null);
    setPainLevel(0);
    setPainType("");
  };

  const handleCancelQualification = () => {
    setIsClosing(true);
    setCanClickToClose(false);

    setTimeout(() => {
      if (pendingPath) {
        setAllPaths(prev => prev.slice(0, -1));
      }

      setShowQualificationWidget(false);
      setIsClosing(false);
      setCanDraw(true);
      setSelectedPathIndex(null);
      setPendingPath(null);
      setPainLevel(0);
      setPainType("");
    }, 200);
  };

  const handleSavePainAssessment = async () => {
    if (allPaths.length === 0) {
      return;
    }

    setIsSaving(true);

    const sanitizedPaths = allPaths.map(({ painTypeId, ...rest }) => ({
      ...rest,
      strokeWidth:
        Number(rest.strokeWidth) ||
        getToolConfig(rest.toolType || DEFAULT_TOOL).strokeWidth,
    }));

    const painAssessmentData = {
      patient_id: existingAssessment?.patient_id || patientData._id,
      gender: existingAssessment?.gender || patientData.gender,
      allPaths: sanitizedPaths,
    };

    try {
      if (isEditMode) {
        await updatePainAssessment(existingAssessment._id, painAssessmentData);
      } else {
        await addPainAssessment(painAssessmentData);
      }
      await refreshPatientContext();
      hidePopup();
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const renderGradientArea = () => {
    return (
      <div className="pain-gradient-area-wrapper">
        <div className="pain-gradient-container">
          <div className="pain-gradient-label pain-gradient-label-top">10</div>
          <div className="pain-gradient-stripe"></div>
          <div className="pain-gradient-label pain-gradient-label-bottom">
            1
          </div>
        </div>
        {/* Tool Selector - Vertical stack next to gradient */}
        <div className="pain-tool-selector-container">
          <PainAssesmentToolSelector
            selectedTool={selectedTool}
            onToolSelect={setSelectedTool}
            isDisabled={!canDraw || showQualificationWidget}
          />
        </div>
      </div>
    );
  };

  const renderQualificationWidget = () => {
    return (
      <div className="pain-qualification-widget">
        <div className="pain-qualification-header">
          <h3 className="pain-qualification-title">
            {t("228")} {/* Pain Level */}
          </h3>

          <IconButton
            icon={IconsEnum.CLOSE}
            onClick={handleCancelQualification}
            aria-label="Close qualification widget"
            noBgColor
          />
        </div>

        {/* Pain Level Selector */}
        <div className="pain-level-section">
          <label className="pain-qualification-label">{t("228")}</label>
          <div className="pain-level-selector">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
              <button
                key={level}
                className={`pain-level-button ${
                  painLevel === level ? "selected" : ""
                }`}
                style={{ backgroundColor: getPainColor(level) }}
                onClick={() => setPainLevel(level)}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Pain Type Selector */}
        <div className="pain-type-section">
          <label className="pain-qualification-label">
            Pain Type (Optional):
          </label>
          <div className="pain-type-selector">
            {painTypesData.map(typeData => {
              const displayText =
                t(typeData.translationKey) || typeData.fallback;
              const isSelected = painType === typeData.id;

              return (
                <button
                  key={typeData.id}
                  className={`pain-type-button ${isSelected ? "selected" : ""}`}
                  onClick={() =>
                    setPainType(prev =>
                      prev === typeData.id ? "" : typeData.id
                    )
                  }
                >
                  {displayText}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pain-qualification-actions">
          {selectedPathIndex !== null && (
            <Button
              onClick={handleDeletePath}
              className="pain-delete-button "
              value={t("210")}
              isAlert={true}
            />
          )}
          <Button
            onClick={handleAddPainQualification}
            disabled={painLevel === 0}
            className="pain-add-button"
            value={selectedPathIndex !== null ? t("51") : t("1")}
          />
        </div>
      </div>
    );
  };

  const renderDrawingArea = () => {
    const svgComponents = getSvgComponents();

    return (
      <div className="pain-drawing-container">
        <div
          className="pain-scalable-drawing-wrapper"
          ref={drawingAreaRef}
          onMouseLeave={() => setHoveredPathInfo(null)}
        >
          <SvgImageCarousel
            svgComponents={svgComponents}
            activeIndex={currentOrientation}
            onSvgClick={handleOrientationChange}
            isDisabled={!canDraw}
            allPaths={allPaths}
            selectedPathIndex={selectedPathIndex}
            onPathClick={handlePathClick}
            onPathAdded={handlePathAdded}
            canDraw={canDraw}
            selectedTool={selectedTool}
            onPathsUpdate={handlePathsUpdate}
            onPathsDeleted={handlePathsDeleted}
            onPathHover={(pathObj, event) => {
              if (!pathObj?.painLevel) return;

              const containerRect =
                drawingAreaRef.current?.getBoundingClientRect();

              const cursorX = event?.clientX ?? 0;
              const cursorY = event?.clientY ?? 0;

              setHoveredPathInfo({
                painLevel: pathObj.painLevel,
                painType: pathObj.painType,
                position: {
                  x: containerRect ? cursorX - containerRect.left : cursorX,
                  y: containerRect ? cursorY - containerRect.top : cursorY,
                },
                viewportPosition: {
                  x: cursorX,
                  y: cursorY,
                },
              });
            }}
            onPathLeave={() => setHoveredPathInfo(null)}
          />
          {hoveredPathInfo && (
            createPortal(
              <div
                className="pain-path-tooltip"
                style={{
                  left: hoveredPathInfo.viewportPosition?.x || 0,
                  top: hoveredPathInfo.viewportPosition?.y || 0,
                  position: "fixed",
                }}
              >
                <div className="pain-path-tooltip-level">
                  Pain level {hoveredPathInfo.painLevel}
                </div>
                <div className="pain-path-tooltip-type">
                  Type: {hoveredPathInfo.painType || "Not specified"}
                </div>
              </div>,
              document.body
            )
          )}
        </div>
        {hasChanges && allPaths.length > 0 && !showQualificationWidget && (
          <Button
            className="pain-save-button"
            value={isSaving ? t("200") : isEditMode ? t("51") : t("151")}
            onClick={handleSavePainAssessment}
            disabled={
              isSaving || allPaths.length === 0 || showQualificationWidget
            }
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div
        className={`pain-assessment-content ${
          showQualificationWidget ? "popup-open" : ""
        }`}
        onClick={e => {
          if (
            showQualificationWidget &&
            canClickToClose &&
            e.target.classList.contains("pain-assessment-content")
          ) {
            handleCancelQualification();
          }
        }}
      >
        <div className="pain-assessment-gradient-area">
          {renderGradientArea()}
        </div>
        <div className="pain-assessment-drawing-area">
          {renderDrawingArea()}
        </div>
        {showQualificationWidget && (
          <div
            className={`pain-assessment-qualification-area ${
              isClosing ? "closing" : ""
            }`}
          >
            {renderQualificationWidget()}
          </div>
        )}
      </div>
    );
  };

  return (
    <ModalBase
      title={t("186")}
      subtitle={isEditMode ? t("51") : t("199")}
      onClose={hidePopup}
      className="pain-assessment-modal"
    >
      {renderContent()}
    </ModalBase>
  );
};

export default PainAssesmentModal;
